import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AxiosError } from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, InlineNotification, Input, Link, RadioGroup, RadioGroupOption, Spinner, Text, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { AuthWrapper, OtpInput } from '../../components';
import { AuthContext, AuthRoute } from '../../features';
import { createMobilePhoneValidator, ensurePhoneUtilsLoaded, getPhoneE164, maskEmail } from '../../features/auth/utils';
import { useCooldown } from '../../hooks';
const RESEND_COOLDOWN_SECONDS = 30;
export const MfaEnrollmentPage = () => {
    const translate = useLocalizeMessage();
    const navigate = useNavigate();
    const { addNotification } = useToastNotification();
    const { mfaChallenge, enrollMfa, verifyMfaEnrollment, clearMfaChallenge, redirectToDefaultPage, isLoading, isLoggedIn, } = useContext(AuthContext);
    const [step, setStep] = useState('method_selection');
    const [selectedMethod, setSelectedMethod] = useState('email');
    const [code, setCode] = useState('');
    const [enrollmentResponse, setEnrollmentResponse] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [cooldown, setCooldown] = useCooldown();
    const [error, setError] = useState(null);
    const hasAutoSelected = useRef(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isPhoneValid, setIsPhoneValid] = useState(false);
    const [phoneLibReady, setPhoneLibReady] = useState(false);
    const phoneValidator = useMemo(() => createMobilePhoneValidator(translate), [phoneLibReady, translate]);
    useEffect(() => {
        void ensurePhoneUtilsLoaded().then((loaded) => {
            if (loaded) {
                setPhoneLibReady(true);
            }
        });
    }, []);
    const allowedMethods = useMemo(() => mfaChallenge?.allowedMfaMethods || [], [mfaChallenge]);
    const getNextStepForMethod = useCallback((method) => {
        if (method === 'totp') {
            return 'totp_qr';
        }
        if (method === 'sms') {
            return 'phone_input';
        }
        return 'confirmation';
    }, []);
    // Handle states where there is no MFA challenge:
    // - Logged in (post-verification): redirect to default page
    // - Not logged in (stale visit): redirect to login
    useEffect(() => {
        if (!mfaChallenge) {
            if (isLoggedIn) {
                void redirectToDefaultPage();
            }
            else {
                navigate(AuthRoute.LOGIN);
            }
        }
    }, [mfaChallenge, isLoggedIn, navigate, redirectToDefaultPage]);
    // If only one method allowed, auto-select and go to appropriate step
    useEffect(() => {
        if (mfaChallenge && allowedMethods.length === 1 && !hasAutoSelected.current) {
            hasAutoSelected.current = true;
            const method = allowedMethods[0];
            setSelectedMethod(method);
            setStep(getNextStepForMethod(method));
        }
        else if (mfaChallenge && allowedMethods.length > 1) {
            setSelectedMethod(allowedMethods[0]);
        }
    }, [allowedMethods, getNextStepForMethod, mfaChallenge]);
    const handleEnroll = async (method, mobile) => {
        const enrollMethod = method || selectedMethod;
        if (isSending) {
            return;
        }
        setIsSending(true);
        setError(null);
        try {
            const response = await enrollMfa(enrollMethod, mobile ? { mobile } : undefined);
            setEnrollmentResponse(response);
            setStep('code_verification');
            if (enrollMethod !== 'totp') {
                setCooldown(RESEND_COOLDOWN_SECONDS);
            }
        }
        catch (err) {
            if (err instanceof AxiosError && err.response?.status === 401) {
                return;
            }
            addNotification({
                title: translate('Error'),
                subtitle: translate('Failed to set up MFA. Please try again.'),
                type: 'error',
            });
        }
        finally {
            setIsSending(false);
        }
    };
    const handleEnrollTotp = useCallback(async () => {
        if (isSending) {
            return;
        }
        setIsSending(true);
        setError(null);
        try {
            const response = await enrollMfa('totp');
            setEnrollmentResponse(response);
        }
        catch (err) {
            if (err instanceof AxiosError && err.response?.status === 401) {
                return;
            }
            addNotification({
                title: translate('Error'),
                subtitle: translate('Failed to set up MFA. Please try again.'),
                type: 'error',
            });
        }
        finally {
            setIsSending(false);
        }
    }, [addNotification, enrollMfa, isSending, translate]);
    // Trigger TOTP enrollment when entering the QR step
    useEffect(() => {
        if (step === 'totp_qr' && !enrollmentResponse) {
            void handleEnrollTotp();
        }
    }, [enrollmentResponse, handleEnrollTotp, step]);
    const handleResend = async () => {
        if (isSending || !enrollmentResponse) {
            return;
        }
        setIsSending(true);
        setError(null);
        setCode('');
        try {
            const mobile = selectedMethod === 'sms' ? getPhoneE164(phoneNumber) : undefined;
            const response = await enrollMfa(selectedMethod, mobile ? { mobile } : undefined);
            setEnrollmentResponse(response);
            setCooldown(RESEND_COOLDOWN_SECONDS);
        }
        catch (err) {
            if (err instanceof AxiosError && err.response?.status === 401) {
                return;
            }
            addNotification({
                title: translate('Error'),
                subtitle: translate('Failed to resend verification code. Please try again.'),
                type: 'error',
            });
        }
        finally {
            setIsSending(false);
        }
    };
    const handleVerify = async () => {
        if (!code.trim() || !enrollmentResponse) {
            return;
        }
        setIsSubmitting(true);
        setError(null);
        try {
            await verifyMfaEnrollment(code.trim(), enrollmentResponse.mfaMethodId);
        }
        catch {
            setError(translate('Incorrect code entered'));
            setCode('');
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleNextFromMethodSelection = () => {
        setStep(getNextStepForMethod(selectedMethod));
    };
    const handleSendCode = async () => {
        await handleEnroll();
    };
    const handleSendCodeWithPhone = async () => {
        if (!isPhoneValid) {
            return;
        }
        await handleEnroll('sms', getPhoneE164(phoneNumber));
    };
    const handleNextFromQr = () => {
        setStep('code_verification');
    };
    const handleBack = () => {
        setError(null);
        setCode('');
        if (step === 'code_verification') {
            if (selectedMethod === 'totp') {
                setStep('totp_qr');
            }
            else if (selectedMethod === 'sms') {
                setStep('phone_input');
            }
            else {
                setStep('confirmation');
            }
        }
        else if (step === 'confirmation' || step === 'phone_input' || step === 'totp_qr') {
            setEnrollmentResponse(null);
            if (allowedMethods.length === 1) {
                clearMfaChallenge();
                navigate(AuthRoute.LOGIN);
            }
            else {
                setStep('method_selection');
            }
        }
    };
    const getMethodLabel = (method) => {
        switch (method) {
            case 'email': {
                return mfaChallenge?.loginEmail
                    ? `${translate('Email to')} ${maskEmail(mfaChallenge.loginEmail)}`
                    : translate('Email');
            }
            case 'sms': {
                return translate('Text message (SMS)');
            }
            case 'totp':
                return translate('Authenticator app');
            default:
                return method;
        }
    };
    const getConfirmationMessage = () => {
        if (selectedMethod === 'email') {
            const masked = mfaChallenge?.loginEmail ? maskEmail(mfaChallenge.loginEmail) : '';
            return `${translate('A verification code will be sent to')} ${masked}.`;
        }
        return '';
    };
    const getTitle = () => {
        if (step === 'method_selection') {
            return translate('Setup two factor authentication');
        }
        switch (selectedMethod) {
            case 'email':
                return translate('Add email verification');
            case 'sms':
                return translate('Add SMS verification');
            case 'totp':
                return translate('Add authenticator app');
            default:
                return translate('Setup two factor authentication');
        }
    };
    if (!mfaChallenge) {
        return (_jsx(AuthWrapper, { title: translate('Setup two factor authentication'), children: _jsx(Spinner, {}) }));
    }
    return (_jsxs(AuthWrapper, { title: getTitle(), onSubmit: step === 'code_verification' ? handleVerify : undefined, showSupportFooter: true, children: [step === 'method_selection' && allowedMethods.length > 1 && (_jsxs("div", { className: 'flex flex-col gap-6', children: [_jsx(Text, { size: 'sm', className: 'text-secondary text-left', children: translate('For your security, you will need to set up two factor authentication to verify your account. Choose how you would like to receive your verification code.') }), _jsx(RadioGroup, { value: selectedMethod, onChange: (value) => setSelectedMethod(value), children: allowedMethods.map((method) => (_jsx(RadioGroupOption, { option: {
                                value: method,
                                label: getMethodLabel(method),
                            } }, method))) }), _jsx(Button, { className: 'w-full', dataTestId: 'mfa-enroll-next-button', label: translate('Next'), onClick: handleNextFromMethodSelection, disabled: isSending || isLoading })] })), step === 'confirmation' && (_jsxs("div", { className: 'flex flex-col gap-6', children: [_jsxs("button", { type: 'button', onClick: handleBack, className: 'flex items-center gap-1 self-start text-sm text-secondary hover:text-primary', children: [_jsx("span", { children: "\u2190" }), " ", translate('Back')] }), _jsx(Text, { size: 'sm', className: 'text-secondary text-left', children: getConfirmationMessage() }), _jsx(Text, { size: 'sm', className: 'text-secondary text-left', children: translate('Press the button below to send your one-time code.') }), _jsx(Button, { className: 'w-full', dataTestId: 'mfa-enroll-send-code-button', label: translate('Send code'), onClick: handleSendCode, disabled: isSending || isLoading })] })), step === 'phone_input' && (_jsxs("div", { className: 'flex flex-col gap-6', children: [_jsxs("button", { type: 'button', onClick: handleBack, className: 'flex items-center gap-1 self-start text-sm text-secondary hover:text-primary', children: [_jsx("span", { children: "\u2190" }), " ", translate('Back')] }), _jsx(Text, { size: 'sm', className: 'text-secondary text-left', children: translate('Enter your phone number below to receive a verification code via text message.') }), _jsx(Input, { id: 'mfa-enrollment-phone', dataTestId: 'mfa-enroll-phone-input', label: translate('Phone number'), type: 'tel', width: 'full', value: phoneNumber, onChange: (e) => setPhoneNumber(e.target.value), validate: phoneValidator, onValidChange: setIsPhoneValid, required: true }), _jsx(Text, { size: 'xs', className: 'text-secondary', children: translate('Message and data rates may apply. Contact your carrier for details.') }), _jsx(Button, { className: 'w-full', dataTestId: 'mfa-enroll-phone-send-code-button', label: translate('Send code'), onClick: handleSendCodeWithPhone, disabled: !isPhoneValid || isSending || isLoading })] })), step === 'totp_qr' && (_jsxs("div", { className: 'flex flex-col gap-6', children: [_jsxs("button", { type: 'button', onClick: handleBack, className: 'flex items-center gap-1 self-start text-sm text-secondary hover:text-primary', children: [_jsx("span", { children: "\u2190" }), " ", translate('Back')] }), _jsx(Text, { size: 'sm', className: 'text-secondary', children: translate('Scan the QR code below and enter the authenticaton app to verify your account.') }), enrollmentResponse?.qrCode && (_jsx("div", { className: 'flex justify-center py-2', children: _jsx("img", { src: enrollmentResponse.qrCode, alt: translate('TOTP QR Code'), width: 200, height: 200 }) })), enrollmentResponse?.totpUri && !enrollmentResponse?.qrCode && (_jsx("div", { className: 'flex justify-center py-2', children: _jsx(QRCodeSVG, { value: enrollmentResponse.totpUri, size: 200 }) })), _jsx(Button, { className: 'w-full', dataTestId: 'mfa-enroll-qr-next-button', label: translate('Next'), onClick: handleNextFromQr })] })), step === 'code_verification' && (_jsxs("div", { className: 'flex flex-col gap-6', children: [_jsxs("button", { type: 'button', onClick: handleBack, className: 'flex items-center gap-1 self-start text-sm text-secondary hover:text-primary', children: [_jsx("span", { children: "\u2190" }), " ", translate('Back')] }), error && _jsx(InlineNotification, { type: 'error', title: error }), _jsx(Text, { size: 'sm', className: 'text-secondary text-left', children: selectedMethod === 'totp'
                            ? translate('Please enter the one-time code displayed in your authenticator app')
                            : `${translate('A verification code has been sent to')} ${selectedMethod === 'sms' ? getPhoneE164(phoneNumber) || phoneNumber : mfaChallenge?.loginEmail || ''}.\n${translate('Please enter the one-time code to finish signing in.')}` }), _jsx(OtpInput, { value: code, onChange: setCode, hasError: !!error }), selectedMethod !== 'totp' && (_jsxs("div", { className: 'flex items-center justify-center gap-2', children: [_jsx(Text, { size: 'sm', className: 'text-secondary', children: translate("Haven't received a code?") }), _jsx(Link, { to: '#', onClick: handleResend, disabled: cooldown > 0 || isSending, children: cooldown > 0 ? `${translate('Resend new')} (${cooldown}s)` : translate('Resend new') })] })), _jsx(Button, { className: 'w-full', dataTestId: 'mfa-enroll-verify-button', label: translate('Submit'), disabled: code.length < 6 || isSubmitting || isLoading })] }))] }));
};
